import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useRegisterChatInput } from '../../hooks/useChatInputRegistry';
import { Send, Zap, CheckCircle, Clock, Paperclip, Mic, ArrowLeft, ChevronDown, ChevronUp, Eye, FileText, BookOpen, Image as ImageIcon, Search, BarChart3, ArrowRight, Plus, X, Brain } from 'lucide-react';
import { AGENT_THUMBS } from './V4BriefingShared';
import { CONTENT_DATA, CONTENT_CHAT_RESPONSES } from '../../data/contentData';
import V4AdHocChat from './V4AdHocChat';
import { buildContentDeepDive, PRIYA_CONTENT_CREATE, PRIYA_CONTENT_KEYPRESS } from '../../data/priyaContentDeepDiveData';

const AGENT_NAMES = { wright: 'Wright', buzz: 'Buzz', marsi: 'Marsi' };

const TYPE_STYLES = {
  Blog: { icon: FileText, dark: 'text-indigo-400 bg-indigo-400/10', light: 'text-indigo-600 bg-indigo-600/10' },
  'Case Study': { icon: BookOpen, dark: 'text-emerald-400 bg-emerald-400/10', light: 'text-emerald-600 bg-emerald-600/10' },
  Infographic: { icon: ImageIcon, dark: 'text-pink-400 bg-pink-400/10', light: 'text-pink-600 bg-pink-600/10' },
  'Release Notes': { icon: FileText, dark: 'text-amber-400 bg-amber-400/10', light: 'text-amber-600 bg-amber-600/10' },
  Whitepaper: { icon: BookOpen, dark: 'text-purple-400 bg-purple-400/10', light: 'text-purple-600 bg-purple-600/10' },
};

const COLUMN_CONFIG = [
  { key: 'draft', label: 'Draft', countColor: 'text-white/40', lightCountColor: 'text-slate-500', dot: 'bg-white/20', lightDot: 'bg-slate-300' },
  { key: 'review', label: 'In Review', countColor: 'text-amber-400', lightCountColor: 'text-amber-600', dot: 'bg-amber-400', lightDot: 'bg-amber-500' },
  { key: 'scheduled', label: 'Scheduled', countColor: 'text-indigo-400', lightCountColor: 'text-indigo-600', dot: 'bg-indigo-400', lightDot: 'bg-indigo-500' },
  { key: 'published', label: 'Published', countColor: 'text-emerald-400', lightCountColor: 'text-emerald-600', dot: 'bg-emerald-400', lightDot: 'bg-emerald-500' },
];

/* ── Content Card ── */
const ContentCard = ({ item, stage, isDark, onApprove, onDetails, onDeepDive, approved }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const cardBg = isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]';
  const hoverBg = isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]';
  const typeStyle = TYPE_STYLES[item.type] || TYPE_STYLES.Blog;
  const TypeIcon = typeStyle.icon;
  const thumb = AGENT_THUMBS[item.agent];
  const isNew = item.isNew;

  return (
    <div className={`${cardBg} border ${isNew ? (isDark ? 'border-indigo-500/25' : 'border-indigo-300/60') : border} ${isNew ? (isDark ? 'bg-indigo-950/20' : 'bg-indigo-50/60') : ''} rounded-lg px-3 py-2.5`} data-testid={`content-${item.id}`}>
      <div className="flex items-center gap-2">
        <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full flex items-center gap-1 ${isDark ? typeStyle.dark : typeStyle.light}`}>
          <TypeIcon className="w-3 h-3" /> {item.type}
        </span>
        {item.seoScore && (
          <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${item.seoScore >= 85 ? (isDark ? 'text-emerald-400 bg-emerald-400/10' : 'text-emerald-600 bg-emerald-600/10') : (isDark ? 'text-amber-400 bg-amber-400/10' : 'text-amber-600 bg-amber-600/10')}`}>
            SEO {item.seoScore}
          </span>
        )}
        {stage === 'review' && !approved && (
          <button onClick={() => onApprove?.(item.id)} className={`ml-auto flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-medium transition-colors ${isDark ? 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20' : 'bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/15'}`} data-testid={`content-approve-${item.id}`}>
            <CheckCircle className="w-3 h-3" /> Approve
          </button>
        )}
        {approved && (
          <span className={`ml-auto text-[9px] font-medium px-1.5 py-0.5 rounded-full flex items-center gap-1 ${isDark ? 'text-emerald-400 bg-emerald-400/10' : 'text-emerald-600 bg-emerald-600/10'}`}>
            <CheckCircle className="w-2.5 h-2.5" /> Approved
          </span>
        )}
      </div>

      <p className={`text-xs font-semibold mt-1.5 ${tp}`}>{item.title}</p>

      <div className="flex items-center gap-2 mt-1">
        {thumb && <img src={thumb} alt="" className="w-3.5 h-3.5 rounded-full object-cover" />}
        <span className={`text-[9px] ${ts}`}>{AGENT_NAMES[item.agent]}</span>
        {item.words && <span className={`text-[9px] ${ts}`}>· {item.words.toLocaleString()} words</span>}
        {item.publishDate && <span className={`text-[9px] ${ts}`}>· {item.publishDate}</span>}
        {item.submittedDate && <span className={`text-[9px] ${ts}`}>· Submitted {item.submittedDate}</span>}
      </div>

      {/* Published metrics */}
      {stage === 'published' && item.views && (
        <div className="flex items-center gap-3 mt-1.5">
          <span className={`text-[9px] ${ts}`}>Views: <span className={`font-medium ${tp}`}>{item.views}</span></span>
          <span className={`text-[9px] ${ts}`}>Avg: <span className={`font-medium ${tp}`}>{item.avgTime}</span></span>
          {item.leads > 0 && <span className={`text-[9px] ${ts}`}>Leads: <span className={`font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{item.leads}</span></span>}
        </div>
      )}

      {/* Keywords */}
      {item.keywords?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {item.keywords.slice(0, 3).map((k, i) => (
            <span key={i} className={`text-[8px] px-1.5 py-0.5 rounded-md ${isDark ? 'bg-white/[0.04] text-white/40' : 'bg-black/[0.03] text-slate-500'}`}>{k}</span>
          ))}
        </div>
      )}

      {/* CTAs */}
      <div className="flex items-center gap-1.5 mt-2">
        <button
          onClick={() => onDeepDive?.(item.id)}
          className={`flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-medium transition-colors ${isDark ? 'bg-indigo-500/15 text-indigo-400 hover:bg-indigo-500/25' : 'bg-indigo-500/10 text-indigo-600 hover:bg-indigo-500/20'}`}
          data-testid={`content-deepdive-${item.id}`}
        >
          <Zap className="w-3 h-3" /> Deep Dive
        </button>
        <button
          onClick={() => onDetails?.(item.id)}
          className={`flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-medium border ${border} ${ts} ${hoverBg}`}
          data-testid={`content-details-${item.id}`}
        >
          <Eye className="w-3 h-3" /> Details
        </button>
      </div>
    </div>
  );
};

/* ── Content Details Panel (slide-in right drawer) ── */
const TYPE_PLACEHOLDER_GRAD = {
  Blog: 'from-indigo-500/30 to-blue-500/20',
  'Case Study': 'from-emerald-500/30 to-teal-500/20',
  Whitepaper: 'from-purple-500/30 to-indigo-500/20',
  Infographic: 'from-pink-500/30 to-orange-400/20',
  'Release Notes': 'from-amber-500/30 to-yellow-500/20',
};

const ContentDetailsPanel = ({ item, stage, isDark, onClose, onApprove, approved }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.08]' : 'border-black/[0.08]';
  const panelBg = isDark ? 'bg-[#0a1628]' : 'bg-[#eee8df]';
  const hoverBg = isDark ? 'hover:bg-white/[0.05]' : 'hover:bg-black/[0.03]';
  const sectionBg = isDark ? 'bg-white/[0.02]' : 'bg-black/[0.02]';
  const [showThinking, setShowThinking] = useState(false);

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  if (!item) return null;
  const typeStyle = TYPE_STYLES[item.type] || TYPE_STYLES.Blog;
  const TypeIcon = typeStyle.icon;
  const thumb = AGENT_THUMBS[item.agent];
  const grad = TYPE_PLACEHOLDER_GRAD[item.type] || TYPE_PLACEHOLDER_GRAD.Blog;
  const isWhitepaperLike = item.type === 'Whitepaper' || item.type === 'Case Study';

  return (
    <>
      <div className={`fixed inset-0 z-40 ${isDark ? 'bg-black/40' : 'bg-black/20'} backdrop-blur-sm`} onClick={onClose} data-testid="content-details-backdrop" />
      <div
        className={`fixed top-0 right-0 z-50 h-full w-[480px] ${panelBg} border-l ${border} overflow-y-auto shadow-2xl`}
        style={{ animation: 'slideInRight 0.25s ease-out' }}
        data-testid={`content-details-panel-${item.id}`}
      >
        {/* Header */}
        <div className={`sticky top-0 ${panelBg} border-b ${border} px-5 py-4 flex items-start gap-3 z-10`}>
          {thumb && <img src={thumb} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0 mt-0.5" />}
          <div className="flex-1 min-w-0">
            <p className={`text-[15px] font-semibold ${tp}`}>{item.title}</p>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full flex items-center gap-1 ${isDark ? typeStyle.dark : typeStyle.light}`}>
                <TypeIcon className="w-3 h-3" /> {item.type}
              </span>
              <span className={`text-[11px] ${ts}`}>{AGENT_NAMES[item.agent]}</span>
              {item.words && <span className={`text-[11px] ${ts}`}>· {item.words.toLocaleString()} words</span>}
              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full capitalize ${isDark ? 'bg-white/[0.05] text-white/60' : 'bg-black/[0.05] text-slate-600'}`}>{stage}</span>
            </div>
          </div>
          <button onClick={onClose} className={`flex-shrink-0 p-1.5 rounded-md ${ts} ${hoverBg}`} data-testid="content-details-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-5">
          {/* Hero preview placeholder */}
          <div className={`w-full rounded-lg overflow-hidden border ${border} bg-gradient-to-br ${grad} ${isWhitepaperLike ? 'aspect-[3/4]' : 'aspect-[16/9]'} flex flex-col items-center justify-center`}>
            <TypeIcon className={`${isWhitepaperLike ? 'w-14 h-14' : 'w-10 h-10'} ${isDark ? 'text-white/40' : 'text-white/70'} mb-2`} />
            {isWhitepaperLike && <p className="text-[11px] font-semibold text-white/90 text-center px-4 leading-tight">{item.title}</p>}
            <p className={`text-[10px] uppercase tracking-wider ${isDark ? 'text-white/50' : 'text-white/80'} mt-1`}>{item.type} preview</p>
          </div>

          {/* Wright's Notes */}
          {item.note && (
            <div>
              <p className={`text-[10px] font-semibold uppercase tracking-wider mb-1.5 ${ts}`}>Wright's Notes</p>
              <div className={`rounded-lg border ${border} p-3 ${sectionBg}`}>
                <p className={`text-[12px] leading-relaxed ${tp}`}>{item.note}</p>
              </div>
            </div>
          )}

          {/* SEO metrics */}
          {item.seoScore && (
            <div>
              <p className={`text-[10px] font-semibold uppercase tracking-wider mb-1.5 ${ts}`}>SEO</p>
              <div className={`flex items-center gap-4 rounded-lg border ${border} p-3 ${sectionBg}`}>
                <div className="flex-shrink-0 flex flex-col items-center justify-center w-16 h-16 rounded-full border-4" style={{ borderColor: item.seoScore >= 85 ? (isDark ? '#34d399' : '#10b981') : '#f59e0b' }}>
                  <p className={`text-lg font-bold ${item.seoScore >= 85 ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-amber-400' : 'text-amber-600')}`}>{item.seoScore}</p>
                  <p className={`text-[8px] uppercase ${ts} -mt-1`}>score</p>
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-[11px] ${tp}`}>{item.seoScore >= 85 ? 'Ship-ready.' : 'Needs one more pass.'}</p>
                  <p className={`text-[10px] ${ts} mt-1`}>Target keywords density passing; meta tags complete.</p>
                </div>
              </div>
            </div>
          )}

          {/* Keywords */}
          {item.keywords?.length > 0 && (
            <div>
              <p className={`text-[10px] font-semibold uppercase tracking-wider mb-1.5 ${ts}`}>Target Keywords</p>
              <div className="flex flex-wrap gap-1.5">
                {item.keywords.map((k, i) => (
                  <span key={i} className={`text-[10px] px-2 py-0.5 rounded-full ${isDark ? 'bg-indigo-500/10 text-indigo-400' : 'bg-indigo-500/10 text-indigo-600'}`}>{k}</span>
                ))}
              </div>
            </div>
          )}

          {/* Published actuals */}
          {stage === 'published' && item.views && (
            <div>
              <p className={`text-[10px] font-semibold uppercase tracking-wider mb-1.5 ${ts}`}>Performance (live)</p>
              <div className={`grid grid-cols-3 gap-2 rounded-lg border ${border} overflow-hidden`}>
                <div className={`px-3 py-2 ${sectionBg}`}>
                  <p className={`text-[10px] ${ts}`}>Views</p>
                  <p className={`text-[14px] font-semibold ${tp} mt-0.5`}>{item.views}</p>
                </div>
                <div className="px-3 py-2">
                  <p className={`text-[10px] ${ts}`}>Avg Time</p>
                  <p className={`text-[14px] font-semibold ${tp} mt-0.5`}>{item.avgTime}</p>
                </div>
                <div className={`px-3 py-2 ${sectionBg}`}>
                  <p className={`text-[10px] ${ts}`}>Leads</p>
                  <p className={`text-[14px] font-semibold ${tp} mt-0.5`}>{item.leads}</p>
                </div>
              </div>
            </div>
          )}

          {/* Schedule / submission dates */}
          <div className={`flex items-center gap-3 text-[11px] ${ts}`}>
            {item.assignedDate && <span>Assigned: <span className={tp}>{item.assignedDate}</span></span>}
            {item.submittedDate && <span>Submitted: <span className={tp}>{item.submittedDate}</span></span>}
            {item.publishDate && <span>Publish: <span className={tp}>{item.publishDate}</span></span>}
          </div>

          {/* Thinking trail */}
          <div>
            <button onClick={() => setShowThinking(!showThinking)} className={`flex items-center gap-1.5 text-[10px] font-medium ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} data-testid={`content-thinking-${item.id}`}>
              <Brain className="w-3 h-3" /> {showThinking ? 'Hide Wright\'s Thinking' : 'Show Wright\'s Thinking'}
            </button>
            {showThinking && (
              <div className={`mt-2 px-3 py-2.5 rounded-lg text-[11px] leading-relaxed ${isDark ? 'bg-indigo-500/5 border border-indigo-500/10 text-indigo-300' : 'bg-indigo-500/5 border border-indigo-500/10 text-indigo-700'}`}>
                {item.thinking || `Researched competitive SERPs for target keywords, mapped reader intent to H2 structure, cross-referenced with ${item.type === 'Whitepaper' ? 'Q4 research data' : 'last 30 days content performance'}. Wright typically does 3 pass-throughs before shipping to Review: structure → keyword density → reader flow.`}
              </div>
            )}
          </div>

          {/* Actions */}
          {stage === 'review' && !approved && (
            <div className="flex items-center gap-2 pt-2">
              <button
                onClick={() => { onApprove?.(item.id); onClose(); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium transition-colors ${isDark ? 'bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25' : 'bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/25'}`}
                data-testid={`content-details-approve-${item.id}`}
              >
                <CheckCircle className="w-3.5 h-3.5" /> Approve & Schedule
              </button>
              <button className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium border ${border} ${ts} ${hoverBg}`}>
                Edit draft
              </button>
            </div>
          )}
        </div>
      </div>
      <style>{`@keyframes slideInRight { from { transform: translateX(20px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>
    </>
  );
};

/* ── SEO Keyword Row ── */
const KeywordRow = ({ kw, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const isNew = kw.change === 'new';
  const isUp = kw.change.startsWith('+');

  return (
    <div className="flex items-center gap-3 py-1.5">
      <span className={`text-xs font-bold w-8 text-center ${tp}`}>#{kw.position}</span>
      <span className={`text-[11px] flex-1 ${tp}`}>{kw.keyword}</span>
      <span className={`text-[10px] ${ts}`}>{kw.volume}</span>
      <span className={`text-[10px] font-medium w-10 text-right ${isNew ? (isDark ? 'text-indigo-400' : 'text-indigo-600') : isUp ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-red-400' : 'text-red-500')}`}>
        {kw.change}
      </span>
    </div>
  );
};

/* ── Activity Item ── */
const ActivityItem = ({ item, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const thumb = AGENT_THUMBS[item.agent];

  return (
    <div className="flex items-start gap-2 py-1.5">
      {thumb && <img src={thumb} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0 mt-0.5" />}
      <div className="flex-1 min-w-0">
        <p className={`text-[11px] leading-relaxed ${tp}`}><span className="font-semibold">{AGENT_NAMES[item.agent]}</span> {item.action}</p>
        <p className={`text-[9px] mt-0.5 ${ts}`}>{item.time}</p>
      </div>
    </div>
  );
};

/* ── Chat Message ── */
const ChatMessage = ({ msg, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  if (msg.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className={`max-w-[75%] px-3.5 py-2.5 rounded-2xl rounded-br-md text-sm leading-relaxed ${isDark ? 'bg-indigo-500/15 text-white' : 'bg-indigo-500/10 text-slate-800'}`}>{msg.text}</div>
      </div>
    );
  }
  return (
    <div className="flex gap-3">
      <img src={AGENT_THUMBS.wright} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0 mt-1" />
      <div className={`text-sm leading-relaxed whitespace-pre-line ${tp}`}>{msg.text}</div>
    </div>
  );
};

/* ════════════════════════════════════════ */
/* ── Main Component ── */
/* ════════════════════════════════════════ */

const V4MktContent = ({ isDark, onWorkflowToggle, workflowPanelOpen, activeUser, priyaState, contentAdHocScenario, onContentAdHocDone }) => {
  const [approvedItems, setApprovedItems] = useState(new Set());
  const [message, setMessage] = useState('');
  useRegisterChatInput(setMessage);
  const [chatMode, setChatMode] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [detailsItemId, setDetailsItemId] = useState(null);
  const [deepDiveItemId, setDeepDiveItemId] = useState(null);
  const [newContentFlow, setNewContentFlow] = useState(false);
  const messagesEndRef = useRef(null);

  const isPriya = activeUser?.id === 'marketing';
  const data = CONTENT_DATA;
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const solidBg = isDark ? 'bg-[#0a1628]' : 'bg-[#eee8df]';
  const inputBorder = isDark ? 'border-white/[0.08]' : 'border-black/[0.08]';
  const hoverBg = isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]';

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  // Merge Priya-created content into pipeline by stage
  const mergedPipeline = useMemo(() => {
    const created = priyaState?.createdContent || {};
    return {
      draft: [...(created.draft || []), ...data.pipeline.draft],
      review: [...(created.review || []), ...data.pipeline.review],
      scheduled: [...(created.scheduled || []), ...data.pipeline.scheduled],
      published: [...(created.published || []), ...data.pipeline.published],
    };
  }, [priyaState?.createdContent, data.pipeline]);

  // Look up a content item across all stages
  const findItem = (id) => {
    for (const stage of ['draft', 'review', 'scheduled', 'published']) {
      const it = mergedPipeline[stage]?.find(i => i.id === id);
      if (it) return { item: it, stage };
    }
    return null;
  };

  const sendMessage = (text, responseData) => {
    const userMsg = { role: 'user', text };
    const agentMsg = { role: 'agent', text: responseData?.text || CONTENT_CHAT_RESPONSES.general.text };
    setChatMessages(prev => [...prev, userMsg, agentMsg]);
    setChatMode(true);
  };

  const handleCreateCopy = () => {
    if (isPriya) {
      setNewContentFlow(true);
      return;
    }
    sendMessage('I want to create a new piece of content.', CONTENT_CHAT_RESPONSES.create);
  };

  const handleBackFromNewContent = () => {
    const scenario = PRIYA_CONTENT_CREATE['new-content'];
    if (scenario?.createdContent && priyaState?.createPriyaContent) {
      priyaState.createPriyaContent(scenario.createdContent, scenario.createdContentStage || 'review');
    }
    setNewContentFlow(false);
  };

  const handleSend = () => { if (message.trim()) { sendMessage(message.trim(), null); setMessage(''); } };

  const handleInput = (e) => {
    const ta = e.target;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 200) + 'px';
    setMessage(e.target.value);
  };

  const totalPieces = Object.values(mergedPipeline).flat().length;

  /* ── Chat input ── */
  const chatInput = (
    <div className="flex justify-center pb-4 px-4 flex-shrink-0">
      <div className="w-[80%]">
        <div className={`${solidBg} border ${inputBorder} rounded-2xl flex flex-col`}>
          <div className="px-4 pt-3 pb-2">
            <textarea value={message} onChange={handleInput} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Message Wright about content..." rows={1}
              className={`w-full bg-transparent resize-none outline-none text-sm leading-5 ${tp} placeholder:${ts}`}
              style={{ height: 'auto', maxHeight: '200px', overflow: message.split('\n').length > 8 ? 'auto' : 'hidden' }}
              data-testid="content-chat-input" />
          </div>
          <div className="flex items-center justify-between px-3 pb-3">
            <div className="flex items-center gap-1">
              <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg ${isDark ? 'bg-white/[0.04]' : 'bg-black/[0.03]'}`}>
                <img src={AGENT_THUMBS.wright} alt="" className="w-5 h-5 rounded-full object-cover" />
                <span className={`text-xs font-medium ${tp}`}>Wright</span>
              </div>
              <button onClick={onWorkflowToggle} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors ${workflowPanelOpen ? (isDark ? 'bg-white/[0.08]' : 'bg-black/[0.06]') : hoverBg}`}>
                <Zap className={`w-3.5 h-3.5 ${tp}`} /><span className={`text-xs font-medium ${tp}`}>Workflows</span>
              </button>
              <button className={`p-1.5 rounded-lg transition-colors ${hoverBg}`}><Paperclip className={`w-3.5 h-3.5 ${ts}`} /></button>
            </div>
            <div className="flex items-center gap-1.5">
              <button className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${hoverBg}`}><Mic className={`w-4 h-4 ${ts}`} /></button>
              <button onClick={handleSend} className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${message.trim() ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : (isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]')}`} data-testid="content-send-btn">
                <Send className={`w-3.5 h-3.5 ${message.trim() ? 'text-white' : ts}`} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  /* ── Deep Dive per content item ── */
  if (deepDiveItemId) {
    const found = findItem(deepDiveItemId);
    const ddScenario = found ? buildContentDeepDive(found.item) : null;
    if (ddScenario) {
      return (
        <V4AdHocChat
          isDark={isDark}
          scenarioId={ddScenario.id}
          scenarios={{ [ddScenario.id]: ddScenario }}
          backLabel="Back to Content"
          onBack={() => setDeepDiveItemId(null)}
        />
      );
    }
  }

  /* ── New Content flow (Priya-only) ── */
  if (newContentFlow && isPriya) {
    return (
      <V4AdHocChat
        isDark={isDark}
        scenarioId="new-content"
        scenarios={PRIYA_CONTENT_CREATE}
        backLabel="Back to Content"
        onBack={handleBackFromNewContent}
      />
    );
  }

  /* ── Keypress A (Priya-only, driven by wrapper) ── */
  if (contentAdHocScenario && isPriya) {
    return (
      <V4AdHocChat
        isDark={isDark}
        scenarioId={contentAdHocScenario}
        scenarios={PRIYA_CONTENT_KEYPRESS}
        backLabel="Back to Content"
        onBack={() => onContentAdHocDone?.()}
        skipFirstUserDelay
      />
    );
  }

  if (chatMode) {
    return (
      <div className="flex flex-col h-full" data-testid="content-chat-view">
        <div className={`px-4 py-3 flex-shrink-0 border-b ${border}`}>
          <button onClick={() => setChatMode(false)} className={`flex items-center gap-2 px-2 py-1 rounded-lg transition-colors ${hoverBg}`} data-testid="content-back-btn">
            <ArrowLeft className={`w-4 h-4 ${ts}`} /><span className={`text-sm font-medium ${tp}`}>Back to Content</span>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto"><div className="flex justify-center"><div className="w-[80%] py-5 space-y-5">
          {chatMessages.map((msg, i) => <ChatMessage key={i} msg={msg} isDark={isDark} />)}
          <div ref={messagesEndRef} />
        </div></div></div>
        {chatInput}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="content-page">
      {/* Header */}
      <div className="px-5 pt-4 pb-3 flex-shrink-0">
        <div className="flex items-center gap-3 mb-1">
          <FileText className={`w-5 h-5 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
          <p className={`text-base font-semibold ${tp}`}>Content</p>
          <span className={`text-[10px] ${ts}`}>{totalPieces} pieces this month</span>
          <button onClick={handleCreateCopy} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-colors bg-gradient-to-br from-indigo-500 to-purple-600 text-white hover:opacity-90 ml-auto" data-testid="content-create-btn">
            <Plus className="w-3.5 h-3.5" /> New Copy
          </button>
        </div>
      </div>

      <div className={`border-t ${border}`} />

      {/* Main: Pipeline + SEO sidebar */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <div className="grid grid-cols-4 gap-0 h-full">
          {/* Kanban columns (3 cols = 4 pipeline stages squeezed in) */}
          <div className="col-span-3 flex min-h-0 overflow-hidden">
            {COLUMN_CONFIG.map((col) => {
              const items = mergedPipeline[col.key];
              return (
                <div key={col.key} className={`flex-1 flex flex-col min-h-0 border-r ${border} last:border-r-0`}>
                  {/* Column header */}
                  <div className="flex items-center gap-2 px-3 py-2.5">
                    <div className={`w-2 h-2 rounded-full ${isDark ? col.dot || '' : col.lightDot || ''}`} />
                    <p className={`text-[11px] font-semibold ${tp}`}>{col.label}</p>
                    <span className={`text-[10px] font-bold ${isDark ? col.countColor : col.lightCountColor}`}>{items.length}</span>
                  </div>
                  {/* Cards */}
                  <div className="flex-1 overflow-y-auto px-2.5 pb-3 space-y-2">
                    {items.map(item => (
                      <ContentCard
                        key={item.id}
                        item={item}
                        stage={col.key}
                        isDark={isDark}
                        onApprove={(id) => setApprovedItems(prev => new Set([...prev, id]))}
                        onDetails={setDetailsItemId}
                        onDeepDive={setDeepDiveItemId}
                        approved={approvedItems.has(item.id)}
                      />
                    ))}
                    {items.length === 0 && (
                      <p className={`text-[10px] text-center py-6 ${ts}`}>No items</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* SEO + Activity sidebar (1 col) */}
          <div className="col-span-1 flex flex-col min-h-0 overflow-y-auto px-3 py-3">
            {/* SEO metrics */}
            <div className="flex items-center gap-2 mb-2">
              <Search className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
              <p className={`text-xs font-semibold ${tp}`}>SEO Snapshot</p>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div className={`px-2 py-1.5 rounded-lg border ${border} ${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'}`}>
                <p className={`text-[8px] ${ts}`}>Organic Traffic</p>
                <p className={`text-sm font-bold ${tp}`}>{data.seo.organicTraffic.value}</p>
                <p className={`text-[8px] ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{data.seo.organicTraffic.trend}</p>
              </div>
              <div className={`px-2 py-1.5 rounded-lg border ${border} ${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'}`}>
                <p className={`text-[8px] ${ts}`}>Keywords Top 20</p>
                <p className={`text-sm font-bold ${tp}`}>{data.seo.keywordsRanking.value}</p>
                <p className={`text-[8px] ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{data.seo.keywordsRanking.trend} new</p>
              </div>
              <div className={`px-2 py-1.5 rounded-lg border ${border} ${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'}`}>
                <p className={`text-[8px] ${ts}`}>Avg Position</p>
                <p className={`text-sm font-bold ${tp}`}>{data.seo.avgPosition.value}</p>
                <p className={`text-[8px] ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{data.seo.avgPosition.trend}</p>
              </div>
            </div>

            {/* Top Keywords */}
            <p className={`text-[10px] font-semibold mb-1.5 ${tp}`}>Top Keywords</p>
            <div className={`flex flex-col divide-y ${isDark ? 'divide-white/[0.04]' : 'divide-black/[0.04]'} mb-4`}>
              {data.seo.topKeywords.map((kw, i) => <KeywordRow key={i} kw={kw} isDark={isDark} />)}
            </div>

            {/* Activity */}
            <div className="flex items-center gap-2 mb-2">
              <Zap className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
              <p className={`text-xs font-semibold ${tp}`}>Wright Activity</p>
            </div>
            <div className={`flex flex-col divide-y ${isDark ? 'divide-white/[0.04]' : 'divide-black/[0.04]'}`}>
              {data.recentActivity.map(item => <ActivityItem key={item.id} item={item} isDark={isDark} />)}
            </div>
          </div>
        </div>
      </div>

      {chatInput}

      {/* Content details drawer */}
      {detailsItemId && (() => {
        const found = findItem(detailsItemId);
        if (!found) return null;
        return (
          <ContentDetailsPanel
            item={found.item}
            stage={found.stage}
            isDark={isDark}
            onClose={() => setDetailsItemId(null)}
            onApprove={(id) => setApprovedItems(prev => new Set([...prev, id]))}
            approved={approvedItems.has(found.item.id)}
          />
        );
      })()}
    </div>
  );
};

export default V4MktContent;
